/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Image from "react-bootstrap/Image";
import moment from 'helpers/moment';
import parse from "html-react-parser";

/* Helpers, constants, custom methods, internal libraries */
import { useAirlineProgramsService } from "../../services/airlineProgramsService.js";
import { useLocaleContext } from "../../contexts/localeContext";
import { UNITED_HOME_LINK } from "../../constants/appConstants";

/* Components */
import UCButton from "../../components/buttons";
import UnitedImageOne from "../../resources/images/unitedBgCarousel1.png";
import UnitedImageTwo from "../../resources/images/unitedBgCarousel2.png";
import CarouselImage3 from "../../resources/images/unitedCarousel1.png";
import CarouselImage6 from "../../resources/images/unitedCarousel2.png";
import JoinUnitedAirlinesProgramModal from "../modals/joinUnitedAirlinesProgramModal/index.js";
import ProgramCarouselModal from "../modals/programCarouselModal";


/* Styles */
import styles from "./styles.js";

const UAProgramContent = ({ delinkUnitedAirlines, unitedAirlinesSupplier, joinUnitedAirlines }) => {
    const theme = useTheme();
    const classes = styles({ theme });
    const [showJoinUnitedAirlinesProgramModal, setShowJoinUnitedAirlinesProgramModal] = useState(false);
    const { getStaticContent, staticContent } = useAirlineProgramsService();
    const locale = useLocaleContext();

    const [showProgramCarousel, setShowProgramCarousel] = useState(false);
    const [showFullText, setShowFullText] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const formatDate = (dateString) => {
        return moment(dateString).locale(locale).format("D MMM 'YY");
    };

    useEffect(() => {
        if (!staticContent || !staticContent?.unitedAirlines) {
            getStaticContent();
        }
    }, [staticContent]);

    const handleShow = (index) => {
        setCurrentIndex(index);
        setShowProgramCarousel(true);
    };

    const handleSelect = (selectedIndex) => {
        setCurrentIndex(selectedIndex);
    };

    const images = [
      CarouselImage3, CarouselImage6
    ];

    const toggleText = () => {
        setShowFullText((prev) => !prev);
    };

    const staticContentUnavailable = Object.keys(staticContent).length === 0;
    const uaContentUnavailable = staticContent?.unitedAirlines?.headerText === "" || staticContent?.unitedAirlines?.text === ""

    const fullText = staticContent?.unitedAirlines?.text || "";
    const maxVisibleChars = 490;

    let buttonInjected = false;

    const renderFormattedText = (text, truncate = false) => {
        const paragraphs = text.split("\n\n");
        let accumulatedLength = 0;
        let isTruncated = false;

        return paragraphs.map((paragraph, idx) => {
            const parsedParagraph = parse(paragraph);

            if (truncate && !isTruncated) {
                const remainingChars = maxVisibleChars - accumulatedLength;

                if (accumulatedLength + paragraph.length > maxVisibleChars) {
                    const truncatedContent = paragraph.slice(0, remainingChars) + "...";
                    isTruncated = true;

                    const truncatedParsedParagraph = parse(truncatedContent);

                    if (truncatedParsedParagraph.type === "ul") {
                        return (
                            <div key={idx} className={classes.customContentTextParagraph}>
                                {injectButtonInList(truncatedParsedParagraph)}
                            </div>
                        );
                    }

                    return (
                        <div key={idx} className={classes.customContentTextParagraph}>
                            {truncatedParsedParagraph}
                        </div>
                    );
                }
            }

            accumulatedLength += paragraph.length;
            return (
                <div
                    key={idx}
                    className={
                        idx === paragraphs.length - 1
                            ? classes.customContentTextLastParagraph
                            : classes.customContentTextParagraph
                    }
                >
                    {injectButtonInList(parsedParagraph)}
                </div>
            );
        });
    };

    const injectButtonInList = (element) => {
        if (!element || element.type !== "ul" || buttonInjected) {
            return element;
        }

        const children = React.Children.toArray(element.props.children);

        if (children.length > 0) {
            const lastChild = children[children.length - 1];

            if (lastChild.type === "li") {
                const newLastChild = React.cloneElement(lastChild, {
                    children: (
                        <>
                            {lastChild.props.children}
                            <button onClick={toggleText} className={classes.inlineReadMoreButton}>
                                <FormattedMessage id="airlineProgram.unitedAirlines.program.readMoreDots" />
                                <FormattedMessage id="airlineProgram.unitedAirlines.program.readMore" />
                            </button>
                        </>
                    ),
                });

                buttonInjected = true;
                return React.cloneElement(element, {
                    children: [...children.slice(0, -1), newLastChild],
                });
            }
        }

        return element;
    };

    return (
        <div className={classes.uaProgramContent}>
            <Col xs={12} className={classes.dividerContainer}>
                <hr className={classes.border} />
            </Col>
            {
                !staticContentUnavailable && !uaContentUnavailable && staticContent?.unitedAirlines ? (
                    <Container className={classes.customContentContainer}>
                        <div>
                            <div className={classes.customContentHeader}>
                                {staticContent?.unitedAirlines?.headerText}
                            </div>
                            <div className={classes.customContentText}>
                                {showFullText ? (
                                    renderFormattedText(fullText)
                                ) : (
                                    renderFormattedText(fullText.slice(0, maxVisibleChars), true)
                                )}

                                {!buttonInjected && (
                                    <button onClick={toggleText} className={classes.inlineReadMoreButton}>
                                        {showFullText ? (
                                            <FormattedMessage id="airlineProgram.unitedAirlines.program.readLess" />
                                        ) : (
                                            <FormattedMessage id="airlineProgram.unitedAirlines.program.readMore" />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </Container>
                ) : null
            }
            <Container className={classes.uaProgramContainer}>
                <Row className={classes.uaProgramRowOne}>
                    <Col className={classes.programWebsiteContainer}>
                        <p className={classes.bookOnText}><FormattedMessage id="airlineProgram.unitedAirlines.program.bookOn" /></p>
                        <a href={UNITED_HOME_LINK} target="_blank" className={classes.programWebsiteText}><FormattedMessage id="airlineProgram.unitedAirlines.program.website" /></a>
                    </Col>
                    {unitedAirlinesSupplier && unitedAirlinesSupplier?.userStatus !== "ACTIVE" && (
                        <UCButton
                            onClick={() => setShowJoinUnitedAirlinesProgramModal(true)}
                            type="primary"
                            className={classes.connectButton}
                            text={<FormattedMessage id="airlineProgram.unitedAirlines.program.connectAccountButton" />}
                        />
                    )}
                    {unitedAirlinesSupplier && unitedAirlinesSupplier?.userStatus === "ACTIVE" && (
                        <>
                            <Col className={classes.programWebsiteContainer}>
                                <p className={classes.accountConnectedText}><FormattedMessage id="airlineProgram.unitedAirlines.program.accountConnected" /></p>
                                <Row xs={12} className={classes.accountDetailsContainer}>
                                    <span className={classes.programAccountNumberText}>{unitedAirlinesSupplier?.membershipNumber}</span><span className={classes.programAccountConnectedDate}>{formatDate(unitedAirlinesSupplier?.dateLinked)}</span>
                                </Row>
                            </Col>
                            <div className={classes.connectButtonContainer}>
                                <UCButton
                                    onClick={() => delinkUnitedAirlines(unitedAirlinesSupplier?.providerEntityID)}
                                    type="link"
                                    className={classes.delinkButton}
                                    text={<FormattedMessage id="airlineProgram.unitedAirlines.program.disconnectAccountButton" />}
                                    loadingStateStyling={classes.loadingState}
                                    spinnerClassName={classes.delinkSpinner}
                                    spinner={{
                                        name: "delinkUnitedSpinner",
                                        size: 20,
                                    }}
                                />
                            </div>
                        </>
                    )}
                </Row>
                <Row className={classes.uaProgramRowTwo}>
                    <Col xs={6}>
                        <Image
                            onClick={() => handleShow(0)}
                            src={UnitedImageOne}
                        />
                    </Col>
                    <Col xs={6}>
                        <Image
                            onClick={() => handleShow(1)}
                            src={UnitedImageTwo}
                        />
                    </Col>
                </Row>
                <Row className={classes.uaProgramRowThree}>
                    <Col xs={12}>
                        <Row className={classes.faqContainer}>
                            <p className={classes.faqTitleText}><FormattedMessage id="airlineProgram.unitedAirlines.program.whatDoINeed" /></p>
                            <p className={classes.faqDescriptionText}>
                                <FormattedMessage id="airlineProgram.unitedAirlines.program.whatDoINeedDescription" />
                                <strong><FormattedMessage id="airlineProgram.unitedAirlines.program.whatDoINeedLink" /></strong>
                            </p>
                        </Row>
                        <Row className={classes.faqContainer}>
                            <p className={classes.faqTitleText}><FormattedMessage id="airlineProgram.unitedAirlines.program.howItWorks" /></p>
                            <p className={classes.faqDescriptionText}>
                                <FormattedMessage id="airlineProgram.unitedAirlines.program.howItWorksDescription1" />
                                <strong> <FormattedMessage id="airlineProgram.unitedAirlines.program.howItWorksProgram" /></strong>
                                <FormattedMessage id="airlineProgram.unitedAirlines.program.howItWorksOption" />
                            </p>
                        </Row>
                    </Col>
                </Row>
            </Container>
            <JoinUnitedAirlinesProgramModal
                showModal={showJoinUnitedAirlinesProgramModal}
                setShowModal={setShowJoinUnitedAirlinesProgramModal}
                unitedAirlinesSupplier={unitedAirlinesSupplier}
                joinUnitedAirlines={joinUnitedAirlines}
            />

            <ProgramCarouselModal
                show={showProgramCarousel}
                setShow={setShowProgramCarousel}
                providerAirline={"united"}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                handleSelect={handleSelect}
                images={images}
            />
        </div>
    );
};

export default UAProgramContent;